<template>
  <div class="rd-screen">
    <report-tools :copy="hasRows" @copy="copyTable">
      <label class="ui-btn ui-btn--sm rd-toggle" :class="{ 'is-on': groupByEmployee }">
        <input v-model="groupByEmployee" type="checkbox" class="rd-visually-hidden" />
        <v-icon size="16" :color="groupByEmployee ? '#9a3412' : '#5f5852'">
          {{ groupByEmployee ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline" }}
        </v-icon>
        {{ $t("group_by_employee") }}
      </label>
    </report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state v-else-if="!hasRows" icon="mdi-cash-off" :title="$t('no_data')" :text="$t('select_date_and_filters')"></report-state>

    <ui-table
      v-else-if="groupByEmployee"
      variant="report"
      :columns="groupedColumns"
      :rows="groupedRows"
      :row-key="(row) => row.key"
      :totals="groupedTotals"
      :total-label="$t('total')"
    ></ui-table>

    <ui-table
      v-else
      variant="report"
      :columns="topupColumns"
      :rows="topups"
      :row-key="(row) => row.key"
      :totals="topupTotals"
      total-key="dateText"
      :total-label="$t('total')"
    ></ui-table>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabCreditTopups from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabCreditTopups.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

/**
 * Dopune kredita firme "više restorana" na računaru: svaka dopuna (datum, radnik, restoran, iznos) ili, uz
 * "Grupiši po radniku", broj dopuna i zbir po radniku. Period čita iz zajedničke trake. Nasleđuje učitavanje, štampu
 * i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopMultiCreditTopups",
  extends: CompanyMultiRestaurantTabCreditTopups,
  mixins: [hasMultiFeed],
  components: { ReportState, ReportTools },
  computed: {
    /** Kolone tabele po radniku. */
    groupedColumns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "name", label: this.$t("employee"), name: true },
        { key: "count", label: this.$t("count"), align: "end", num: true },
        { key: "amount", label: `${this.$t("amount")}${this.currencySuffix}`, align: "end", num: true, format: this.money },
      ];
    },
    /** Kolone tabele svih dopuna. */
    topupColumns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "dateText", label: this.$t("date"), num: true },
        { key: "name", label: this.$t("employee"), name: true },
        ...(this.hasBranches ? [{ key: "branch", label: this.$t("branch"), muted: true }] : []),
        { key: "restaurantText", label: this.$t("restaurant"), cellClass: (row) => ({ "rd-muted": !row.restaurantId }) },
        { key: "amount", label: `${this.$t("amount")}${this.currencySuffix}`, align: "end", num: true, format: this.money },
      ];
    },
    groupedTotals() {
      return { count: this.topups.length, amount: this.grandTotal };
    },
    topupTotals() {
      return { amount: this.grandTotal };
    },
    busy() {
      return this.loading;
    },
    hasRows() {
      return this.creditTopups.length > 0;
    },
    currencySuffix() {
      return this.majorityRestaurantCurrency ? ` (${this.majorityRestaurantCurrency})` : "";
    },
    /** Dopune za prikaz: datum i restoran već u tekstu (sve restorane ima dopuna bez restorana). */
    topups() {
      return this.creditTopups.map((item, index) => ({
        key: `${item.id || "t"}-${index}`,
        dateText: this.localeDate(item.date),
        name: this.toLatin(item.user_name || ""),
        branch: item.company_name || "",
        restaurantId: item.restaurant_id,
        restaurantText: item.restaurant_id ? this.toLatin(item.restaurant_name) : this.$t("all_restaurants"),
        amount: item.total_amount,
      }));
    },
    groupedRows() {
      return this.groupedTopups.map((item, index) => ({
        key: `${item.user_name}-${index}`,
        name: this.toLatin(item.user_name || ""),
        count: item.topup_count,
        amount: item.total_amount,
      }));
    },
    kpis() {
      return [
        { key: "topups", label: this.$t("total_topups"), value: this.creditTopups.length, tone: "inverse" },
        { key: "amount", label: this.$t("amount"), value: this.money(this.grandTotal) },
        { key: "workers", label: this.$t("report_desktop.workers"), value: this.groupedTopups.length },
      ];
    },
  },
  methods: {
    money(value) {
      return (Number(value) || 0).toFixed(2);
    },
    async copyTable() {
      const headers = this.groupByEmployee
        ? [this.$t("employee"), this.$t("count"), this.$t("amount")]
        : [
            this.$t("date"),
            this.$t("employee"),
            ...(this.hasBranches ? [this.$t("branch")] : []),
            this.$t("restaurant"),
            this.$t("amount"),
          ];
      const rows = this.groupByEmployee
        ? this.groupedRows.map((row) => [row.name, row.count, this.money(row.amount)])
        : this.topups.map((row) => [
            row.dateText,
            row.name,
            ...(this.hasBranches ? [row.branch] : []),
            row.restaurantText,
            this.money(row.amount),
          ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
